import React, { useState, useEffect, useCallback } from 'react';
import {
  FiFilter,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiMail,
  FiShield,
  FiUserCheck,
  FiUserX,
  FiDownload,
  FiUpload,
  FiMoreVertical,
  FiX,
  FiSearch
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { ADMIN_MODULES_FLAT } from '../../constants/adminModules';
import PhoneInput from '../../components/common/PhoneInput';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    dateRange: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [importing, setImporting] = useState(false);
  const { showError, showSuccess } = useNotification();

  const roles = [
    { value: 'student', label: 'Student' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'parent', label: 'Parent' },
    { value: 'admin', label: 'Admin' },
    { value: 'staff', label: 'Staff' }
  ];

  const statuses = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending Verification' }
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({
        search: searchTerm,
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      const normalizeUser = (u) => ({
        id: u._id || u.id,
        _id: u._id || u.id,
        name: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        avatar: u.avatar,
        phone: u.phoneNumber || u.phone,
        role: u.role,
        status: u.status || (u.isActive === false ? 'inactive' : 'active'),
        allowedModules: u.allowedModules || [],
        createdAt: u.createdAt,
        lastActive: u.lastLogin || u.lastActive
      });

      const rawUsers = (response?.data?.data) || [];
      setUsers(rawUsers.map(normalizeUser));
      setPagination(prev => ({
        ...prev,
        total: response?.data?.pagination?.total || 0
      }));
    } catch (error) {
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, pagination.page, pagination.limit, showError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleUserAction = async (action, userId) => {
    try {
      switch (action) {
        case 'activate':
          await adminAPI.updateUserStatus(userId, 'active');
          showSuccess('User activated successfully');
          break;
        case 'suspend':
          await adminAPI.updateUserStatus(userId, 'suspended');
          showSuccess('User suspended successfully');
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this user?')) {
            await adminAPI.deleteUser(userId);
            showSuccess('User deleted successfully');
          }
          break;
        case 'resetPassword':
          await adminAPI.resetUserPassword(userId);
          showSuccess('Password reset email sent');
          break;
        default:
          break;
      }
      fetchUsers();
    } catch (error) {
      showError(`Failed to ${action} user`);
    }
    setActionMenuOpen(null);
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      showError('Please select users first');
      return;
    }

    try {
      switch (action) {
        case 'activate':
          await adminAPI.bulkUpdateUsers(selectedUsers, { status: 'active' });
          showSuccess(`${selectedUsers.length} users activated`);
          break;
        case 'suspend':
          await adminAPI.bulkUpdateUsers(selectedUsers, { status: 'suspended' });
          showSuccess(`${selectedUsers.length} users suspended`);
          break;
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
            await adminAPI.bulkDeleteUsers(selectedUsers);
            showSuccess(`${selectedUsers.length} users deleted`);
          }
          break;
        default:
          break;
      }
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      showError(`Failed to ${action} users`);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      active: 'bg-emerald-100 text-emerald-800',
      inactive: 'bg-slate-100 text-slate-600',
      suspended: 'bg-red-100 text-red-800',
      pending: 'bg-amber-100 text-amber-800'
    };
    const label = { active: 'Active', inactive: 'Inactive', suspended: 'Suspended', pending: 'Pending' }[status] || status;
    return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-600'}`}>{label}</span>;
  };

  const getRoleBadge = (role) => {
    const map = {
      admin: 'bg-red-100 text-red-800',
      staff: 'bg-violet-100 text-violet-800',
      teacher: 'bg-indigo-100 text-indigo-800',
      parent: 'bg-sky-100 text-sky-800',
      student: 'bg-emerald-100 text-emerald-800'
    };
    const label = { admin: 'Admin', staff: 'Staff', teacher: 'Teacher', parent: 'Parent', student: 'Student' }[role] || role;
    return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${map[role] || 'bg-slate-100 text-slate-600'}`}>{label}</span>;
  };

  const [addForm, setAddForm] = useState({ firstName: '', lastName: '', email: '', role: 'student', password: '', phone: '', allowedModules: [] });
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const openAddModal = () => {
    setAddForm({ firstName: '', lastName: '', email: '', role: 'student', password: '', phone: '', allowedModules: [] });
    setAddModalOpen(true);
  };
  const closeAddModal = () => setAddModalOpen(false);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.firstName?.trim() || !addForm.lastName?.trim() || !addForm.email?.trim()) {
      showError('First name, last name and email are required');
      return;
    }
    setAddSubmitting(true);
    try {
      const payload = {
        firstName: addForm.firstName.trim(),
        lastName: addForm.lastName.trim(),
        email: addForm.email.trim(),
        role: addForm.role
      };
      if (addForm.password?.trim()) payload.password = addForm.password.trim();
      if (addForm.phone?.trim()) payload.phone = addForm.phone.trim();
      if (addForm.role === 'staff' && Array.isArray(addForm.allowedModules)) payload.allowedModules = addForm.allowedModules.filter(Boolean);
      await adminAPI.createUser(payload);
      showSuccess('User created successfully');
      closeAddModal();
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setAddSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'student',
      phone: user.phone || '',
      allowedModules: Array.isArray(user.allowedModules) ? [...user.allowedModules] : []
    });
  };
  const closeEditModal = () => { setEditUser(null); setEditForm(null); };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editUser || !editForm) return;
    if (!editForm.firstName?.trim() || !editForm.lastName?.trim() || !editForm.email?.trim()) {
      showError('First name, last name and email are required');
      return;
    }
    setEditSubmitting(true);
    try {
      const payload = {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        email: editForm.email.trim(),
        role: editForm.role
      };
      if (editForm.phone?.trim()) payload.phone = editForm.phone.trim();
      if (editForm.role === 'staff' && Array.isArray(editForm.allowedModules)) payload.allowedModules = editForm.allowedModules.filter(Boolean);
      await adminAPI.updateUser(editUser.id, payload);
      showSuccess('User updated successfully');
      closeEditModal();
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setEditSubmitting(false);
    }
  };

  const toggleModule = (path, formKind) => {
    if (formKind === 'add') {
      setAddForm(prev => ({
        ...prev,
        allowedModules: prev.allowedModules.includes(path) ? prev.allowedModules.filter(p => p !== path) : [...prev.allowedModules, path]
      }));
    } else if (editForm) {
      setEditForm(prev => ({
        ...prev,
        allowedModules: prev.allowedModules.includes(path) ? prev.allowedModules.filter(p => p !== path) : [...prev.allowedModules, path]
      }));
    }
  };

  const handleExport = () => {
    const headers = ['Name', 'Email', 'Phone', 'Role', 'Status', 'Created'];
    const rows = users.map(u => [u.name, u.email || '', u.phone || '', u.role, u.status, u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '']);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    showSuccess('Export downloaded');
  };

  const handleImport = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = ev.target?.result || '';
        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length < 2) { showError('CSV must have header and at least one row'); setImporting(false); return; }
        const header = lines[0].toLowerCase();
        const cols = header.includes('email') ? (header.includes('first') ? ['firstName', 'lastName', 'email', 'role'] : ['name', 'email', 'role']) : null;
        if (!cols) { showError('CSV must include email column'); setImporting(false); return; }
        let created = 0, failed = 0;
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].match(/("([^"]*)")|([^,]+)/g)?.map(s => (s.startsWith('"') ? s.slice(1, -1).replace(/""/g, '"') : s.trim())) || [];
          const email = values[1] ?? values[cols.indexOf('email')];
          const firstName = values[0] ?? values[cols.indexOf('firstName')] ?? values[0]?.split(/\s+/)[0] ?? '';
          const lastName = values[1] !== undefined && cols.includes('lastName') ? values[2] : (values[0]?.split(/\s+/).slice(1).join(' ') ?? '');
          const role = (values[cols.indexOf('role')] || 'student').toLowerCase();
          if (!email || !firstName) { failed++; continue; }
          try {
            await adminAPI.createUser({ firstName: firstName.trim(), lastName: (lastName || '').trim(), email: email.trim(), role: ['student', 'teacher', 'parent', 'admin', 'staff'].includes(role) ? role : 'student' });
            created++;
          } catch (_) { failed++; }
        }
        showSuccess(`Import done: ${created} created, ${failed} failed`);
        fetchUsers();
      } catch (err) { showError('Import failed'); }
      setImporting(false);
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Users</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage system users and permissions.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={handleExport} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors">
              <FiDownload className="h-4 w-4" /> Export
            </button>
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer">
              <FiUpload className="h-4 w-4" /> Import
              <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} />
            </label>
            <button type="button" onClick={openAddModal} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
              <FiPlus className="h-4 w-4" /> Add user
            </button>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              placeholder="Search by name, email..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showFilters ? 'bg-slate-100 border-slate-300 text-slate-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              <FiFilter className="h-4 w-4" /> More
            </button>
            {showFilters && (
              <>
                <select value={filters.role} onChange={(e) => handleFilterChange('role', e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30">
                  <option value="all">All roles</option>
                  {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30">
                  <option value="all">All statuses</option>
                  {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select value={filters.dateRange} onChange={(e) => handleFilterChange('dateRange', e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30">
                  <option value="all">All time</option>
                  <option value="today">Today</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                  <option value="quarter">This quarter</option>
                </select>
              </>
            )}
          </div>
          {users.length > 0 && <span className="text-xs text-slate-500 sm:ml-auto">{users.length} of {pagination.total}</span>}
        </div>

        {selectedUsers.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-3 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm">
            <span className="text-sm font-medium text-slate-700">{selectedUsers.length} selected</span>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => handleBulkAction('activate')} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"><FiUserCheck className="h-4 w-4" /> Activate</button>
              <button type="button" onClick={() => handleBulkAction('suspend')} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"><FiUserX className="h-4 w-4" /> Suspend</button>
              <button type="button" onClick={() => handleBulkAction('delete')} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-colors"><FiTrash2 className="h-4 w-4" /> Delete</button>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiUserCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No users found.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80">
                      <th className="px-4 py-3 w-10 text-left"><input type="checkbox" checked={selectedUsers.length === users.length && users.length > 0} onChange={handleSelectAll} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400" /></th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">User</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Role</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Registered</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-700">Last active</th>
                      <th className="px-4 py-3 text-right font-medium text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3"><input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => handleUserSelect(user.id)} className="rounded border-slate-300 text-slate-700 focus:ring-slate-400" /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 shrink-0 overflow-hidden">
                              {user.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : (user.name && user.name.charAt(0).toUpperCase())}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 truncate">{user.name}</div>
                              <div className="text-slate-500 truncate">{user.email}</div>
                              {user.phone && <div className="text-xs text-slate-400">{user.phone}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                        <td className="px-4 py-3">{getStatusBadge(user.status)}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(user.createdAt)}</td>
                        <td className="px-4 py-3 text-slate-600">{user.lastActive ? formatDate(user.lastActive) : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" onClick={() => setViewUser(user)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="View"><FiEye className="h-4 w-4" /></button>
                            <button type="button" onClick={() => openEditModal(user)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Edit"><FiEdit className="h-4 w-4" /></button>
                            <button type="button" onClick={() => user.email && window.open(`mailto:${user.email}`)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Email"><FiMail className="h-4 w-4" /></button>
                            <div className="relative">
                              <button type="button" onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="More"><FiMoreVertical className="h-4 w-4" /></button>
                              {actionMenuOpen === user.id && (
                                <div className="absolute right-0 top-full mt-1 py-1 w-40 rounded-lg border border-slate-200 bg-white shadow-lg z-10">
                                  {user.status !== 'active' && <button type="button" onClick={() => handleUserAction('activate', user.id)} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><FiUserCheck className="h-4 w-4" /> Activate</button>}
                                  {user.status === 'active' && <button type="button" onClick={() => handleUserAction('suspend', user.id)} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><FiUserX className="h-4 w-4" /> Suspend</button>}
                                  <button type="button" onClick={() => handleUserAction('resetPassword', user.id)} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"><FiShield className="h-4 w-4" /> Reset password</button>
                                  <button type="button" onClick={() => handleUserAction('delete', user.id)} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><FiTrash2 className="h-4 w-4" /> Delete</button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Page {pagination.page} of {totalPages} · {pagination.total} users</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Previous</button>
                    <button type="button" onClick={() => setPagination(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))} disabled={pagination.page >= totalPages} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add User Modal */}
        {addModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeAddModal}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Add user</h2>
                <button type="button" onClick={closeAddModal} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><FiX className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleAddSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First name</label>
                  <input type="text" value={addForm.firstName} onChange={e => setAddForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Last name</label>
                  <input type="text" value={addForm.lastName} onChange={e => setAddForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone (optional)</label>
                  <PhoneInput value={addForm.phone} onChange={(v) => setAddForm(f => ({ ...f, phone: v }))} placeholder="e.g. 8012345678" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg">
                    {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {addForm.role === 'staff' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Allowed modules (admin sidebar)</label>
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                      {ADMIN_MODULES_FLAT.map(m => (
                        <label key={m.path} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={addForm.allowedModules.includes(m.path)} onChange={() => toggleModule(m.path, 'add')} className="rounded border-slate-300" />
                          <span className="text-sm text-slate-700">{m.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password (optional)</label>
                  <input type="password" value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} placeholder="Leave blank for auto-generated" className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <button type="button" onClick={closeAddModal} className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
                  <button type="submit" disabled={addSubmitting} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">{addSubmitting ? 'Creating...' : 'Create user'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editUser && editForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeEditModal}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Edit user</h2>
                <button type="button" onClick={closeEditModal} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><FiX className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleEditSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First name</label>
                  <input type="text" value={editForm.firstName} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Last name</label>
                  <input type="text" value={editForm.lastName} onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone (optional)</label>
                  <PhoneInput value={editForm.phone} onChange={(v) => setEditForm(f => ({ ...f, phone: v }))} placeholder="e.g. 8012345678" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg">
                    {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {editForm.role === 'staff' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Allowed modules</label>
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                      {ADMIN_MODULES_FLAT.map(m => (
                        <label key={m.path} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={editForm.allowedModules.includes(m.path)} onChange={() => toggleModule(m.path, 'edit')} className="rounded border-slate-300" />
                          <span className="text-sm text-slate-700">{m.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 justify-end pt-2">
                  <button type="button" onClick={closeEditModal} className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
                  <button type="submit" disabled={editSubmitting} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">{editSubmitting ? 'Saving...' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View User Modal */}
        {viewUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewUser(null)}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">User details</h2>
                <button type="button" onClick={() => setViewUser(null)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><FiX className="h-5 w-5" /></button>
              </div>
              <div className="p-4 space-y-3 text-sm">
                <p><span className="font-medium text-slate-600">Name:</span> {viewUser.name}</p>
                <p><span className="font-medium text-slate-600">Email:</span> {viewUser.email || '—'}</p>
                <p><span className="font-medium text-slate-600">Phone:</span> {viewUser.phone || '—'}</p>
                <p><span className="font-medium text-slate-600">Role:</span> {getRoleBadge(viewUser.role)}</p>
                <p><span className="font-medium text-slate-600">Status:</span> {getStatusBadge(viewUser.status)}</p>
                {viewUser.role === 'staff' && Array.isArray(viewUser.allowedModules) && viewUser.allowedModules.length > 0 && (
                  <p><span className="font-medium text-slate-600">Modules:</span> {viewUser.allowedModules.map(p => ADMIN_MODULES_FLAT.find(m => m.path === p)?.label || p).join(', ')}</p>
                )}
                <p><span className="font-medium text-slate-600">Registered:</span> {formatDate(viewUser.createdAt)}</p>
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
                <button type="button" onClick={() => setViewUser(null)} className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">Close</button>
                <button type="button" onClick={() => { setViewUser(null); openEditModal(viewUser); }} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-800">Edit</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;